import { Component, DestroyRef, ElementRef, inject, Inject, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { GlassGuideService } from '../../glass-guide.service';
import { Make, Model, Variant } from '../../../model/glass-guide.model';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { UserService } from '../../../services/user.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import moment from 'moment';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { MatCheckbox } from '@angular/material/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-vehicle-selection',
  templateUrl: './vehicle-selection.component.html',
  styleUrls: ['./vehicle-selection.component.scss'],
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatCheckbox,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatDialogActions,
    MatButton
  ]
})
export class VehicleSelectionComponent implements OnInit {
  vehicleSelectionForm: UntypedFormGroup;

  makes = signal<Make[]>([]);
  models = signal<Model[]>([]);
  years = signal<number[]>([]);
  variants = signal<Variant[]>([]);

  lastUpdateDate = signal<string>('');
  pending = signal<boolean>(false);
  initializeDataFromVehicle = signal(false);
  selectedVehicle = signal<string | null>('');

  displayedColumns: string[] = ['checkBox', 'name', 'bodyType', 'doors', 'transmission', 'month', 'engine'];
  variantDataSource: MatTableDataSource<Variant>;

  #destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<VehicleSelectionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    private glassGuideService: GlassGuideService,
    private userService: UserService,
    private message: MessagingService,
    public toolbarService: ToolbarService,
    private escapeDialogRef: EscapeDialogHelper,
    private elementRef: ElementRef
  ) {}

  get make() {
    return this.vehicleSelectionForm.get('make');
  }
  get model() {
    return this.vehicleSelectionForm.get('model');
  }
  get year() {
    return this.vehicleSelectionForm.get('year');
  }
  get filter() {
    return this.vehicleSelectionForm.get('filter');
  }
  get unlistedGlassVehicle() {
    return this.vehicleSelectionForm.get('unlistedGlassVehicle');
  }

  ngOnInit(): void {
    const { unlistedGlassVehicle, vehicleSelectedFromGlassGuide, makeId, modelId, vehicleYear } = this.data ?? {};

    this.vehicleSelectionForm = this.fb.group({
      make: makeId,
      model: modelId,
      year: vehicleYear,
      filter: '',
      unlistedGlassVehicle: unlistedGlassVehicle || false
    });

    if (vehicleSelectedFromGlassGuide && makeId && modelId && vehicleYear) {
      this.initializeDataFromVehicle.set(true);
      this.getVariants();
    }

    this.model.disable();
    this.year.disable();
    this.escapeDialogRef.escapeDialog(this.dialogRef);

    this.glassGuideService
      .getMakes()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (makes) => {
          this.makes.set(makes);
          if (this.initializeDataFromVehicle()) {
            this.model.enable();
            this.year.enable();
            this.getModels();
          }
        },
        error: () => this.message.error('Could not retrieve the list of makes.')
      });
    this.glassGuideService
      .getLastUpdateDate()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (lastUpdateDate) => this.lastUpdateDate.set(lastUpdateDate),
        error: () => this.message.error('Could not retrieve the glass guide last update.')
      });

    this.unlistedGlassVehicle.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
      if (value) {
        this.selectedVehicle.set(null);
      }
    });
  }

  selectMake() {
    this.model.setValue('');
    this.year.setValue('');
    this.getModels();
    this.getYears();
    this.model.enable();
    this.year.enable();
    this.variants.set([]);
  }

  getModels() {
    this.glassGuideService
      .getMakeModels(this.make.value)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (models) => {
          this.models.set(models);
          if (this.initializeDataFromVehicle()) {
            this.getModelYears();
          }
        },
        error: () => this.message.error('Could not retrieve the list of models.')
      });
  }

  getYears() {
    this.glassGuideService
      .getMakeYears(this.make.value)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (years) => this.years.set(years),
        error: () => this.message.error('Could not retrieve the list of years.')
      });
  }

  getModelYears() {
    this.glassGuideService
      .getModelYears(this.make.value, this.model.value)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (years) => {
          this.years.set(years);
          this.initializeDataFromVehicle.set(false);
        },
        error: () => this.message.error('Could not retrieve the list of years.')
      });
  }

  getYearModels() {
    this.glassGuideService
      .getYearModels(this.make.value, this.year.value)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (models) => this.models.set(models),
        error: () => this.message.error('Could not retrieve the list of models.')
      });
  }

  getVariants() {
    if (this.make.value && this.model.value && this.year.value) {
      this.glassGuideService
        .getVariants(this.make.value, this.model.value, this.year.value)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (variants) => {
            this.variants.set(variants);
            if (this.initializeDataFromVehicle()) {
              this.selectedVehicle.set(this.data.nvic);
              this.scrollToSelectedRow();
            }
          },
          error: () => this.message.error('Could not retrieve the list of variants.'),
          complete: () => (this.variantDataSource = new MatTableDataSource(this.variants()))
        });
    }
  }

  applyFilter() {
    this.variantDataSource.filter = this.filter.value.trim().toLowerCase();
  }

  selectVehicle() {
    this.pending.set(true);
    this.userService
      .updateJob(
        this.selectedVehicle() || this.unlistedGlassVehicle.value,
        this.data.jobId,
        this.selectedVehicle() ? 'nvic' : 'unlistedGlassVehicle'
      )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => this.dialogRef.close('Success'),
        error: () => {
          this.message.error('Could not select vehicle.');
          this.pending.set(false);
        },
        complete: () => {
          this.message.confirm('Vehicle selected.');
          this.pending.set(false);
        }
      });
  }

  private scrollToSelectedRow() {
    if (this.selectedVehicle()) {
      setTimeout(() => {
        this.elementRef.nativeElement
          .querySelector(`#variant-${this.selectedVehicle()}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }

  formatMonth = (month: number) =>
    moment()
      .month(month - 1)
      .format('MMM');
}
